import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const params = new URLSearchParams({
          page: pagination.page.toString(),
          limit: '20'
        });
        if (statusFilter) params.append('status', statusFilter);

        const response = await api.get(`/blog/admin/all?${params}`);
        setPosts(response.data.posts);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error('Failed to fetch posts:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, [pagination.page, statusFilter]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/blog/admin/${id}`);
      setPosts(posts.filter(p => p._id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (post) => {
    if (post.status === 'draft') {
      return <span className="badge badge-warning">Draft</span>;
    }
    if (post.status === 'archived') {
      return <span className="badge badge-secondary">Archived</span>;
    }
    if (post.status === 'published') {
      const isLive = post.publishDate && new Date(post.publishDate) <= new Date();
      return isLive 
        ? <span className="badge badge-success">Live</span>
        : <span className="badge badge-info">Scheduled</span>;
    }
    return <span className="badge">{post.status}</span>;
  };

  const getCategoryLabel = (value) => {
    const categories = {
      'invoicing': 'Invoicing Basics & Best Practices',
      'bill-of-sale': 'Bill of Sale Articles',
      'online-safety': 'Online Safety & Scam Prevention',
      'buying-selling': 'Buying and Selling Tips',
      'business': 'General Business Advice',
      'thrifting': 'Thrifting to Marketplace Pipeline'
    };
    return categories[value] || value;
  };

  return (
    <AdminLayout>
      <div className="admin-blog">
        <div className="admin-toolbar">
          <div className="admin-toolbar-left">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(p => ({ ...p, page: 1 }));
              }}
              className="admin-select"
            >
              <option value="">All Status</option>
              <option value="draft">Drafts</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            <span className="admin-count">{pagination.total} posts</span>
          </div>
          <Link to="/admin/blog/new" className="admin-btn admin-btn-primary">
            + New Post
          </Link>
        </div>

        {loading ? (
          <div className="admin-loading">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="admin-empty">
            <p>No blog posts found.</p>
            <Link to="/admin/blog/new" className="admin-btn admin-btn-primary">
              Create Your First Post
            </Link>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Publish Date</th>
                  <th>Views</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map(post => (
                  <tr key={post._id}>
                    <td>
                      <Link to={`/admin/blog/${post._id}`} className="post-title-link">
                        {post.title}
                      </Link>
                      <span className="post-slug">/{post.slug}</span>
                    </td>
                    <td>{getCategoryLabel(post.category)}</td>
                    <td>{getStatusBadge(post)}</td>
                    <td>{formatDate(post.publishDate)}</td>
                    <td>{post.views?.toLocaleString() || 0}</td>
                    <td>
                      <div className="admin-actions">
                        <Link 
                          to={`/admin/blog/${post._id}`}
                          className="admin-btn admin-btn-sm"
                        >
                          Edit
                        </Link>
                        {post.status === 'published' && post.publishDate && new Date(post.publishDate) <= new Date() && (
                          <a
                            href={`/blog/${post.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="admin-btn admin-btn-sm admin-btn-secondary"
                          >
                            View
                          </a>
                        )}
                        <button
                          onClick={() => setDeleteConfirm(post._id)}
                          className="admin-btn admin-btn-sm admin-btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="admin-pagination">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="admin-btn admin-btn-sm"
            >
              Previous
            </button>
            <span>Page {pagination.page} of {pagination.pages}</span>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page >= pagination.pages}
              className="admin-btn admin-btn-sm"
            >
              Next
            </button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <h3>Delete Post?</h3>
              <p>This action cannot be undone.</p>
              <div className="admin-modal-actions">
                <button 
                  onClick={() => setDeleteConfirm(null)}
                  className="admin-btn"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleDelete(deleteConfirm)}
                  className="admin-btn admin-btn-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .admin-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        .admin-toolbar-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .admin-select {
          padding: 0.5rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: white;
        }
        .admin-count {
          color: #6b7280;
          font-size: 0.9rem;
        }
        .admin-btn {
          padding: 0.5rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          text-decoration: none;
          color: #374151;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .admin-btn:hover {
          background: #f9fafb;
        }
        .admin-btn-primary {
          background: var(--primary-color);
          border-color: var(--primary-color);
          color: white;
        }
        .admin-btn-primary:hover {
          background: var(--primary-hover);
        }
        .admin-btn-secondary {
          background: #6b7280;
          border-color: #6b7280;
          color: white;
        }
        .admin-btn-danger {
          background: #dc2626;
          border-color: #dc2626;
          color: white;
        }
        .admin-btn-sm {
          padding: 0.25rem 0.75rem;
          font-size: 0.85rem;
        }
        .admin-table-container {
          overflow-x: auto;
        }
        .admin-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
        }
        .admin-table th,
        .admin-table td {
          padding: 1rem;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }
        .admin-table th {
          background: #f9fafb;
          font-weight: 600;
          color: #374151;
        }
        .post-title-link {
          color: var(--primary-color);
          text-decoration: none;
          font-weight: 500;
        }
        .post-title-link:hover {
          text-decoration: underline;
        }
        .post-slug {
          display: block;
          font-size: 0.8rem;
          color: #9ca3af;
        }
        .admin-actions {
          display: flex;
          gap: 0.5rem;
        }
        .badge {
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .badge-success {
          background: #dcfce7;
          color: #166534;
        }
        .badge-warning {
          background: #fef3c7;
          color: #92400e;
        }
        .badge-info {
          background: #dbeafe;
          color: #1e40af;
        }
        .badge-secondary {
          background: #f3f4f6;
          color: #4b5563;
        }
        .admin-empty {
          text-align: center;
          padding: 3rem;
          background: white;
          border-radius: 8px;
        }
        .admin-empty p {
          margin-bottom: 1rem;
          color: #6b7280;
        }
        .admin-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 1.5rem;
        }
        .admin-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .admin-modal {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          max-width: 400px;
          width: 90%;
        }
        .admin-modal h3 {
          margin-bottom: 0.5rem;
        }
        .admin-modal p {
          color: #6b7280;
          margin-bottom: 1.5rem;
        }
        .admin-modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminBlog;
